import React, { useState, useEffect } from 'react';
import api from '../services/api';

export default function PatientDocuments({ practiceId, patientId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { url, name, type }
  const [linkIssue, setLinkIssue] = useState(null); // { name, url, message } when a tab could not be opened

  useEffect(() => {
    if (!practiceId || !patientId) return;
    setLoading(true);
    api.getPatientFiles(practiceId, patientId)
      .then(res => {
        setFiles(res.data || res.files || []);
        setError(null);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [practiceId, patientId]);

  if (loading) return <p style={s.empty}>Loading documents...</p>;
  if (error) return <p style={s.empty}>Error: {error}</p>;
  if (files.length === 0) return <p style={s.empty}>No documents uploaded for this patient.</p>;

  const getExt = (name) => (name || '').split('.').pop().toLowerCase();

  const getIcon = (name) => {
    const ext = getExt(name);
    if (['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(ext)) return '🖼️';
    if (ext === 'pdf') return '📑';
    if (ext === 'html') return '📋';
    return '📄';
  };

  const isPreviewable = (name) => {
    const ext = getExt(name);
    return ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'html'].includes(ext);
  };

  const isImage = (name) => {
    const ext = getExt(name);
    return ['jpg', 'jpeg', 'png', 'webp'].includes(ext);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // commons #277: PDFs used to render in an <iframe> inside the modal, which
  // Chrome blocks ("This page has been blocked by Chrome") for a cross-origin
  // presigned S3 URL, so staff saw an empty box. PDFs now open in a new tab
  // and use the browser's own viewer; images keep the in-app modal.
  //
  // 2026-10-06: the URL is now minted when you CLICK, not when the list loads.
  // listPatientFiles signs every row with a 15-minute expiry at load time, so
  // opening a patient's Documents tab and coming back later gave S3 403
  // "Request has expired". The only console evidence was a second 403 for
  // /favicon.ico on the S3 origin, which hid the real cause completely.
  //
  // The blank tab is opened BEFORE awaiting the URL, deliberately. Awaiting
  // first breaks the user-gesture chain and Chrome blocks the popup — that
  // would have swapped the expiry bug for a silent one. And the handle is only
  // returned when 'noopener' is NOT passed, so the opener is cleared manually
  // instead; the new tab still gets no reference back to this one.
  const openPreview = async (file, { forceNewTab = false } = {}) => {
    const name = file.name || file.key?.split('/').pop() || 'Document';
    if (!file.key || (!forceNewTab && !isPreviewable(name))) return;

    const useModal = isImage(name) && !forceNewTab;
    const tab = useModal ? null : window.open('', '_blank');
    if (tab) tab.opener = null;
    setLinkIssue(null);
    try {
      const res = await api.getPatientFileUrl(practiceId, patientId, file.key);
      const url = res?.data?.url;
      if (!url) throw new Error('No download URL returned');
      if (useModal) {
        setPreview({ url, name, type: 'image' });
        return;
      }
      tab ? (tab.location = url) : setLinkIssue({ name, url });
    } catch (err) {
      if (tab) tab.close();
      setLinkIssue({ name, url: null, message: err.message });
    }
  };

  return (
    <div style={{ padding: '8px 0' }}>
      <h4 style={s.title}>Patient Documents ({files.length})</h4>
      {/* Only shown when the tab could not be opened for us — a blocked popup, or a
          failure fetching the signed URL. Previously both failed silently. */}
      {linkIssue && (
        <div style={s.issue}>
          {linkIssue.url ? (
            <>Your browser blocked the new tab for <strong>{linkIssue.name}</strong>.{' '}
              <a href={linkIssue.url} target="_blank" rel="noopener noreferrer">Open it here</a>.</>
          ) : (
            <>Could not open <strong>{linkIssue.name}</strong>{linkIssue.message ? `: ${linkIssue.message}` : ''}.</>
          )}
        </div>
      )}
      {files.map((file, idx) => {
        const name = file.name || file.key?.split('/').pop() || 'Document';
        const canPreview = !!file.key && isPreviewable(name);
        return (
          <div key={idx} style={{ ...s.fileRow, cursor: canPreview ? 'pointer' : 'default' }}
            onClick={() => canPreview && openPreview(file)}>
            <span style={s.icon}>{getIcon(name)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...s.fileName, color: canPreview ? '#4f46e5' : '#111827' }}>{name}</div>
              <div style={s.fileMeta}>
                {formatSize(file.size)}{file.lastModified ? ` · ${formatDate(file.lastModified)}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {canPreview && (
                <button style={s.previewBtn} onClick={(e) => { e.stopPropagation(); openPreview(file); }}>
                  View
                </button>
              )}
              {file.key && (
                <button style={s.downloadBtn}
                  onClick={(e) => { e.stopPropagation(); openPreview(file, { forceNewTab: true }); }}>
                  Download
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Preview Modal */}
      {preview && (
        <div style={s.overlay} onClick={() => setPreview(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <span style={s.modalTitle}>{preview.name}</span>
              <button style={s.closeBtn} onClick={() => setPreview(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <img src={preview.url} alt={preview.name} style={s.previewImage} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  empty: { color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 20 },
  title: { fontSize: 14, fontWeight: 600, color: '#111827', margin: '0 0 10px' },
  fileRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' },
  icon: { fontSize: 20, flexShrink: 0 },
  fileName: { fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileMeta: { fontSize: 11, color: '#6b7280' },
  previewBtn: { padding: '4px 10px', fontSize: 12, border: '1px solid #4f46e5', borderRadius: 4, color: '#4f46e5', background: '#fff', cursor: 'pointer', flexShrink: 0 },
  downloadBtn: { padding: '4px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, color: '#374151', background: '#fff', cursor: 'pointer', flexShrink: 0 },
  issue: { margin: '0 0 10px', padding: '8px 10px', fontSize: 12, borderRadius: 4, background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 8, width: '90vw', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' },
  modalTitle: { fontSize: 14, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  closeBtn: { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: '0 4px' },
  modalBody: { flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, minHeight: 300 },
  previewImage: { maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 4 },
};
